import './StatisticsObjectChart' // dummy import to avoid module resolution error
import { Line } from 'react-chartjs-2'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'

interface InfrastructureObject {
  id: number
  name: string
  infrastructure_type: string
  location?: string
  readings_count: number
  last_reading_at?: string
}

interface Reading {
  id?: number
  object_id?: number
  object_name: string
  infrastructure_type: string
  reading_type: string
  reading_type_label: string
  value?: number
  datetime?: string
  reading_datetime?: string
  date?: string  // For usage mode
  usage?: number // For usage mode
  user_name?: string
  notes?: string
}

interface ReadingTypeLabels {
  [key: string]: string
}

interface StatisticsObjectChartProps {
  object: InfrastructureObject
  readings: Reading[]
  readingTypeLabels: ReadingTypeLabels
  viewMode?: 'readings' | 'usage'
}

// Define colors for different reading types
const READING_TYPE_COLORS = {
  water_to_network: '#2563eb', // blue-600
  well_water: '#dc2626',      // red-600
  waste_water: '#f59e0b',     // amber-500
  buffer_input: '#7c3aed',    // violet-600
  network_output: '#059669',  // emerald-600
}

export default function StatisticsObjectChart({ object, readings, readingTypeLabels, viewMode = 'readings' }: StatisticsObjectChartProps) {
  // Debug: pokazuje w konsoli jaki tryb jest używany
  console.log('StatisticsObjectChart - viewMode:', viewMode, 'readings count:', readings.length)
  readings.forEach((r, i) => console.log(`Reading ${i}:`, r))
  const isReadingsMode = viewMode === 'readings'

  // Group readings by reading type
  const readingsByType = readings.reduce((acc, reading) => {
    if (!acc[reading.reading_type]) {
      acc[reading.reading_type] = []
    }
    acc[reading.reading_type].push(reading)
    return acc
  }, {} as { [key: string]: Reading[] })

  // Sort readings by appropriate field
  Object.keys(readingsByType).forEach(type => {
    readingsByType[type].sort((a, b) => {
      const dateStrA = isReadingsMode ? a.datetime : a.date
      const dateStrB = isReadingsMode ? b.datetime : b.date
      const dateA = dateStrA ? new Date(dateStrA).getTime() : 0
      const dateB = dateStrB ? new Date(dateStrB).getTime() : 0
      return dateA - dateB
    })
  })

  // Prepare data for chart - different logic for readings vs usage
  const labels = Array.from(
    new Set(
      readings.flatMap(r => {
        try {
          if (isReadingsMode) {
            // For readings: show date + time
            const dateTime = r.datetime ? new Date(r.datetime) : null
            if (!dateTime || isNaN(dateTime.getTime())) throw new Error('Invalid datetime')
            return [dateTime.toISOString().split('T')[0] + ' ' + dateTime.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })]
          } else {
            // For usage: show date only
            const dateValue = r.date ? new Date(r.date) : null
            if (!dateValue || isNaN(dateValue.getTime())) throw new Error('Invalid date')
            return [dateValue.toISOString().split('T')[0]]
          }
        } catch (error) {
          console.warn('Invalid date value for reading:', r, error)
          return []
        }
      })
    )
  ).sort()

  // Create datasets
  const datasets = Object.entries(readingsByType).map(([readingType, typeReadings]) => {
    const color = READING_TYPE_COLORS[readingType as keyof typeof READING_TYPE_COLORS] || '#6b7280'

    return {
      label: readingTypeLabels[readingType] || readingType,
      data: labels.map(label => {
        const reading = typeReadings.find(r => {
          try {
            if (isReadingsMode) {
              const dateTime = r.datetime ? new Date(r.datetime) : null
              if (!dateTime || isNaN(dateTime.getTime())) return false
              const dateLabel = dateTime.toISOString().split('T')[0] + ' ' + dateTime.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
              return dateLabel === label
            } else {
              const dateValue = r.date ? new Date(r.date) : null
              if (!dateValue || isNaN(dateValue.getTime())) return false
              const dateLabel = dateValue.toISOString().split('T')[0]
              return dateLabel === label
            }
          } catch (error) {
            return false
          }
        })
        return reading ? (isReadingsMode ? reading.value : reading.usage) : null
      }),
      borderColor: color,
      backgroundColor: color,
      pointBackgroundColor: color,
      pointBorderColor: color,
      tension: 0.4,
      fill: false,
      spanGaps: false,
    }
  })

  const data = {
    labels,
    datasets,
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        }
      },
      title: {
        display: true,
        text: `${object.name} - ${object.infrastructure_type}`,
        font: {
          size: 14,
        }
      },
      tooltip: {
        callbacks: {
          title: function(tooltipItems: any) {
            return format(new Date(labels[tooltipItems[0].dataIndex]), isReadingsMode ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy', { locale: pl })
          },
          label: function(context: any) {
            const unit = isReadingsMode ? 'm³' : 'm³/dzień'
            return `${context.dataset.label}: ${context.parsed.y} ${unit}`
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Czas',
        },
        ticks: {
          maxTicksLimit: 10,
          callback: (value: any) => {
            // Show date every few ticks to avoid overcrowding
            if (typeof value === 'number' && labels[value]) {
              const date = new Date(labels[value])
              return format(date, isReadingsMode ? 'dd.MM HH:mm' : 'dd.MM', { locale: pl })
            }
            return ''
          },
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: isReadingsMode ? 'Ilość wody [m³]' : 'Zużycie wody [m³/dzień]',
        },
        beginAtZero: true,
      },
    },
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{object.name}</h3>
        <p className="text-sm text-gray-600">
          {object.infrastructure_type}
          {object.location && ` • ${object.location}`}
        </p>
        <div className="text-xs text-gray-500 mt-1">
          {readings.length} {isReadingsMode ? 'odczytów' : 'okresów zużycia'} • wyświetlane w trybie {viewMode === 'readings' ? 'odczytów' : 'zużycia'}
        </div>
      </div>

      {datasets.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          Brak danych do wyświetlenia w wybranym zakresie
        </div>
      ) : (
        <div style={{ height: '400px' }}>
          <Line data={data} options={options} />
        </div>
      )}

      {/* Legend summary */}
      {datasets.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-700">
            <strong>{isReadingsMode ? 'Odczyty:' : 'Zużycie:'}</strong>
            <div className="mt-1 flex flex-wrap gap-4">
              {datasets.map(dataset => (
                <div key={dataset.label} className="flex items-center">
                  <div
                    className="w-3 h-3 rounded mr-2"
                    style={{ backgroundColor: dataset.borderColor }}
                  ></div>
                  <span>{dataset.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
